/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Why choose FalcoCut section with 6 feature cards
 */
import Link from 'next/link';
import React from 'react';

interface ReasonCard {
  icon: string;
  title: string;
  description: string;
}

const reasons: ReasonCard[] = [
  {
    icon: '🎨',
    title: 'Create from Images or Text',
    description:
      'Start with what you already have — or generate something new. Turn images into videos, reuse existing footage, or generate visuals from scratch without relying on external tools.',
  },
  {
    icon: '🔄',
    title: 'Reuse Content Across Platforms',
    description:
      "One idea doesn't belong to just one platform. FalcoCut helps you adapt the same content for TikTok, ads, product pages, and social channels — without recreating everything from zero.",
  },
  {
    icon: '✨',
    title: 'Content That Feels Natural',
    description:
      'No stiff visuals or robotic results. From visuals to voice, your content stays natural and authentic — even when transformed or localized.',
  },
  {
    icon: '⚡',
    title: 'Built for Speed and Iteration',
    description:
      'Create, test, and refine faster. FalcoCut removes friction so you can iterate on content quickly and respond to what works.',
  },
  {
    icon: '🔓',
    title: 'Creative Freedom Without Lock-In',
    description:
      "You're never boxed into one format or outcome. FalcoCut lets ideas evolve naturally without forcing a fixed structure.",
  },
  {
    icon: '💡',
    title: 'Built to Reduce Creative Fatigue',
    description:
      'Spend less time repeating work and more time creating. FalcoCut helps keep the creative process lightweight and sustainable.',
  },
];

// 箭头图标
const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

const ReasonCardItem: React.FC<ReasonCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-[rgba(255,255,255,0.055)] rounded-2xl p-6 pad:p-8 shadow-sm border border-white/10">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-white mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-white/65 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-black py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-white mb-4 pad:mb-5 leading-tight">
          Why Choose FalcoCut
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-white/65 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut is built for modern content creation — from generating visuals to
          turning them into videos, and reusing everything across platforms. Create once,
          adapt endlessly, and scale without starting over.
        </p>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6 mb-10 pad:mb-14">
          {reasons.map((reason, index) => (
            <ReasonCardItem
              key={index}
              icon={reason.icon}
              title={reason.title}
              description={reason.description}
            />
          ))}
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <Link
            href="/ai-video-generator"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{
              background: 'linear-gradient(90deg, #4D2FFF 0%, #EF5CFF 100%)',
            }}
          >
            <span>Create TikTok Videos</span>
            <ArrowRightIcon />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
